import { useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import {
  Paperclip,
  Plus,
  Trash2,
  FileText,
  Image as ImageIcon,
  File,
} from 'lucide-react'
import {
  LIMITE_ARCHIVO_MB,
  formatFileSize,
  esArchivoPermitido,
  esTamanoPermitido,
} from '@/core/hooks/useDocumentos'
import { toast } from 'sonner'

interface AdjuntosPendientesProps {
  files: File[]
  onChange: (files: File[]) => void
  className?: string
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <ImageIcon className="h-4 w-4 text-blue-500" />
  if (mimeType === 'application/pdf') return <FileText className="h-4 w-4 text-red-500" />
  return <File className="h-4 w-4 text-gray-400" />
}

/**
 * Selector de archivos en memoria (upload diferido). NO sube nada: acumula los
 * archivos elegidos para que el caller los suba+vincule recién cuando la entidad
 * existe (ej. al crear una tarea, ya con su id). Espejo de TareaAdjuntos pero sin
 * llamadas a Storage. Ver ORR-148.
 */
export function AdjuntosPendientes({ files, onChange, className }: AdjuntosPendientesProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleClickSubir = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (!esArchivoPermitido(file)) {
        toast.error('Tipo de archivo no permitido')
      } else if (!esTamanoPermitido(file)) {
        toast.error(`El archivo excede el limite de ${LIMITE_ARCHIVO_MB}MB`)
      } else {
        onChange([...files, file])
      }

      // Limpiar input para permitir re-seleccionar el mismo archivo
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [files, onChange]
  )

  const handleQuitar = useCallback(
    (index: number) => {
      onChange(files.filter((_, i) => i !== index))
    },
    [files, onChange]
  )

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Paperclip className="h-4 w-4" />
        <h4 className="text-sm font-medium flex-1">
          Archivos adjuntos {files.length > 0 && `(${files.length})`}
        </h4>
      </div>

      {/* Input oculto para subir archivos */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
      />

      {/* Lista de archivos pendientes */}
      {files.length > 0 && (
        <div className="space-y-2 mb-3">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="group flex items-center gap-2 p-2 rounded-lg border bg-background hover:bg-muted/50 transition-colors"
            >
              {getFileIcon(file.type)}

              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleQuitar(index)}
                title="Quitar"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Boton para agregar */}
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-muted-foreground"
        onClick={handleClickSubir}
      >
        <Plus className="h-4 w-4 mr-2" />
        Adjuntar archivo
      </Button>
    </div>
  )
}
